import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useSearchParams } from "react-router";

import type { LoginStep, Session } from "@/auth/types";
import { useAuth } from "@/auth/use-auth";
import { Button } from "@/components/ui/button";
import { env, isDev } from "@/lib/env";

import {
  AuthField,
  AuthLayout,
  FormAlert,
  PasswordField,
  safeNext,
  useAuthErrorText,
} from "./components";
import { EnrollStep } from "./EnrollStep";
import { PasswordChangeForm } from "./PasswordChangeForm";
import { TotpStep } from "./TotpStep";

interface LocationState {
  from?: string;
}

type Stage = { kind: "credentials" } | Exclude<LoginStep, { kind: "session" }>;

/**
 * `/login` (T11-02, design/08 §1): e-mail + password → (6-digit code | first-time 2-step setup |
 * new password for a temporary one) → the page the staff member asked for (`?next=`).
 * Errors never say which of e-mail/password was wrong, and never use system words.
 */
export function LoginPage() {
  const { t } = useTranslation("auth");
  const { state, signIn, acceptSession, api } = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  const errorText = useAuthErrorText();
  const [stage, setStage] = useState<Stage>({ kind: "credentials" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const next = safeNext(params.get("next") ?? (location.state as LocationState | null)?.from);

  if (state.status === "authenticated") {
    return <Navigate to={next} replace />;
  }

  const restart = (message: string | null = null) => {
    setStage({ kind: "credentials" });
    setPassword("");
    setError(message);
  };

  const follow = (step: LoginStep | null) => {
    if (!step) {
      restart();
      return;
    }
    if (step.kind === "session") {
      acceptSession(step.session);
    } else {
      setStage(step);
    }
  };

  const onCredentials = async (event: FormEvent) => {
    event.preventDefault();
    if (pending) {
      return;
    }
    if (!email.trim() || !password) {
      setError(t("errors.invalid"));
      return;
    }
    setPending(true);
    setError(null);
    try {
      const step = await signIn({ email, password });
      if (step.kind !== "session") {
        setStage(step);
      }
    } catch (err: unknown) {
      setError(errorText(err));
    } finally {
      setPending(false);
    }
  };

  if (stage.kind === "mfa") {
    return (
      <AuthLayout title={t("totp.title")}>
        <TotpStep
          onSubmit={async (code) => {
            const session: Session = await api.verifyMfa(stage.mfaToken, code);
            acceptSession(session);
          }}
          onBack={() => {
            restart();
          }}
        />
      </AuthLayout>
    );
  }

  if (stage.kind === "enroll") {
    return (
      <AuthLayout title={t("setup.title")}>
        <EnrollStep
          start={() => api.startEnrollment(stage.enrollToken)}
          confirm={(code) => api.confirmEnrollment(stage.enrollToken, code)}
          onDone={(result) => {
            if (result.session) {
              acceptSession(result.session);
            } else {
              restart();
            }
          }}
        />
      </AuthLayout>
    );
  }

  if (stage.kind === "password_change") {
    return (
      <AuthLayout title={t("change.title")} intro={t("change.intro")}>
        <PasswordChangeForm
          withCurrent
          currentLabel={t("change.current")}
          onSubmit={async ({ currentPassword, newPassword }) => {
            follow(
              await api.changePassword({
                currentPassword,
                newPassword,
                changeToken: stage.changeToken,
              }),
            );
          }}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("login.title")} intro={t("login.intro")}>
      <form
        onSubmit={(e) => void onCredentials(e)}
        className="flex flex-col gap-4"
        noValidate
        data-step="credentials"
      >
        <AuthField
          label={t("login.email")}
          name="email"
          type="email"
          autoComplete="username"
          inputMode="email"
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
        <PasswordField
          label={t("login.password")}
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
        <FormAlert message={error} />
        <Button type="submit" size="lg" disabled={pending}>
          {pending ? t("login.submitting") : t("login.submit")}
        </Button>
        <Link
          to="/forgot"
          className="min-h-touch self-start py-2 text-sm underline underline-offset-4"
        >
          {t("login.forgot")}
        </Link>
        {isDev && env.VITE_AUTH_DEV_BYPASS ? (
          <p className="text-xs text-muted-foreground">{t("login.dev_bypass_hint")}</p>
        ) : null}
      </form>
    </AuthLayout>
  );
}
